import { Suspense as DefaultSuspense, useReducer } from 'react'
import clsx from 'clsx'
import { Trans } from '@lingui/react/macro'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import * as Dialog from '@radix-ui/react-dialog'
import * as Sentry from '@sentry/react'

import Loading from './Loading'

export const Suspense: React.FC<
  React.PropsWithChildren<{
    isFullPage?: boolean
    scopeTags?: Record<string, string>
    isDialog?: boolean
  }>
> = function ({ children, isDialog, isFullPage, scopeTags }) {
  const [resetKey, reset] = useReducer(x => x + 1, 0)

  function LoadingFallback() {
    return (
      <div
        className={clsx(
          'flex items-center justify-center py-5',
          isFullPage && 'size-full'
        )}
      >
        {isDialog && (
          <>
            <VisuallyHidden asChild>
              <Dialog.Title>
                <Trans>Loading dialog</Trans>
              </Dialog.Title>
            </VisuallyHidden>
            <VisuallyHidden asChild>
              <Dialog.Description>
                <Trans>Currently loading the dialog content</Trans>
              </Dialog.Description>
            </VisuallyHidden>
          </>
        )}
        <Loading />
      </div>
    )
  }

  return (
    <Sentry.ErrorBoundary
      beforeCapture={scope => {
        if (!scopeTags) return

        Object.entries(scopeTags).forEach(([key, value]) =>
          scope.setTag(key, value)
        )
      }}
      fallback={({ error, resetError }) => {
        return (
          <div
            className={clsx(
              'flex items-center justify-center',
              isFullPage && 'size-full'
            )}
          >
            <div
              role="alert"
              className="mb-12 flex w-full flex-col gap-2 p-4 text-center"
            >
              <p className="text-nav-foreground">
                <Trans>Something went wrong</Trans>:
              </p>
              <p className="text-nav-foreground">{error!.toString()}</p>
              <button
                className="border-none text-primary"
                onClick={() => {
                  resetError()
                  // Compleley remount all children, so that it basically tries again.
                  reset()
                }}
              >
                <Trans>Reset</Trans>
              </button>
            </div>
          </div>
        )
      }}
      showDialog
    >
      <DefaultSuspense key={resetKey} fallback={<LoadingFallback />}>
        {children}
      </DefaultSuspense>
    </Sentry.ErrorBoundary>
  )
}
